import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import { CampaignContext } from "./campaign-context";
import { addTemplate, updateTemplate, ExtendedTemplate, loadTemplates, removeTemplate } from "./extented-template-api";
import { useChangeConfirm, useSelectTemplate, useSetChanged, useSetTemplates, useTemplateContext } from './extended-template-store';
import { Loading } from '../components/Loading';
import { ReactTooltip } from 'tooltip';
import { VariablePicker } from '../audience/message-preview/VariablePicker';
import { MemberNavigation, PreviewMessage } from '../audience/message-preview/MessagePreview';
import { SavingButton } from '../components/SavingButton';
import { sleep } from 'simple-utils';
import { MediaMenu } from '../media/media-selector';
import { useConfirm } from '../components/Dialogs';

var idzeira = 1;

function TemplatesTip(){
    return <>
        <p>As campanhas podem ter até 4 mensagens adicionais.</p>
        <p>Cria mensagens adicionais quando for necessário enviar mais de uma mensagem num único disparo.</p>
        <p className='mb-0'>As mensagens adicionais podem ter texto (com suporte a variáveis) e (imagem ou áudio).</p>
    </>
}

export function MenuTitle(){
    return <div className='mb-2'>Mensagens Adicionais:
        <ReactTooltip className='ms-2' tooltip={<TemplatesTip />} placement='bottom' delay={300} >
            <a className='link d-block pt-2'><i className='fa fa-circle-question'></i></a>
        </ReactTooltip>
    </div>
}

export function MenuBody(){
    const {templates, selected} = useTemplateContext();
    const canAdd = useMemo(()=> {
        if(!templates) return false;
        if(templates.length >= 4) return false;
        return !templates.some(t => t.id == 0)
    }, [templates])
    const {state: {viewCampaign}} = useContext(CampaignContext);
    
    const selectTemplate = useSelectTemplate();
    const index = useMemo(() => selected === null ? 0 : templates.findIndex(t => t.id == selected) + 2, [templates, selected]);
    const setTemplates = useSetTemplates();
    const setChanged = useSetChanged();
    
    useTemplatesLoader(templates);

    const addTemplate = useChangeConfirm(() => {
        const temp:ExtendedTemplate = {id: 0, media: null, campaign: viewCampaign.id, message: ''}
        setTemplates([...templates, temp])
        selectTemplate(temp.id);
    });

    const pickTemplate = useChangeConfirm((id:number) => {
        selectTemplate(id);
    });

    const confirmDelete = useConfirm(index ? <>Deseja apagar o template <b>{index}</b>?</> : "", {})

    async function deleteTemplate(){
        const ok = await confirmDelete()
        if(!ok) return;
        const toDelete = templates.find(t => t.id == selected)
        setChanged(false);
        setTemplates(templates.filter(t => t.id != selected))
        selectTemplate(null);

        if(toDelete)
            await removeTemplate(toDelete);
    }

    if(!templates) return <Loading className='mb-3' />
    if(templates.length == 0)
        return <div className='mb-3 text-center'>
            <button className='btn btn-primary' onClick={addTemplate}>Adicionar mensagem extra</button>
        </div>
    
    return <div className='mb-3 d-flex'>
        <button key="main" onClick={() => pickTemplate(null)} className={`btn ${selected === null ? 'btn-primary' : 'btn-outline-primary'} me-2`}>Principal</button>
        {templates.map((t, i) => <button key={t.id} onClick={() => pickTemplate(t.id)} className={`btn ${selected === t.id ? 'btn-primary' : 'btn-outline-primary'} me-2`}>{i + 2}</button>)}
        <div className='flex-grow-1'></div>
        {index > 0 && <button key="del" className={`btn btn-danger ${canAdd ? 'me-2' : ''}`} onClick={deleteTemplate}>
                <i className='fa fa-trash'></i>
         </button>}
        {canAdd && <>
            <button key="add" className="btn btn-primary" onClick={addTemplate}>
                <i className='fa fa-plus'></i>
            </button>
        </>}
    </div>
}


export function ExtendedTemplateMenu(){
    return <>
        <MenuTitle />
        <MenuBody />
    </>
}

export function ExtendedTemplateEditor({template, variables, navigation}:{template: ExtendedTemplate, variables:string[], navigation:MemberNavigation}){
    const textRef = useRef<HTMLTextAreaElement>(null);
    const [message, setMessage] = useState(template.message)
    const [media, setMedia] = useState(template.media)
    const [saving, setSaving] = useState(false)
    const [nav, setNav] = useState(navigation)
    const {templates, changed} = useTemplateContext();
    const setTemplates = useSetTemplates();
    const selectTemplate = useSelectTemplate();
    const setChanged = useSetChanged();

    useEffect(() => {
        setMedia(template.media)
        setMessage(template.message)
        setChanged(false);
    }, [template])

    useEffect(() => {
        setChanged(message != template.message || media != template.media);
    }, [media, message])

    async function save(){
        setSaving(true)
        await sleep(2);

        if(template.id == 0){
            let {id} = await addTemplate({... template, media, message})
            setTemplates(templates.map(t => t.id == template.id ? {... template, id, media, message} : t))
            selectTemplate(id);
        } else {
            await updateTemplate({... template, media, message})
            setTemplates(templates.map(t => t.id == template.id ? {... template, media, message} : t))
        }

        setSaving(false)
    }

    return <>
        <ExtendedTemplateMenu />

        <div className='mt-3 mb-1'>Mídia:</div>
        <MediaMenu value={media} onChange={n => setMedia(n)} />
        <div className='mt-3'></div>
        
        <VariablePicker variables={variables} setText={setMessage} textRef={textRef} />
        <textarea ref={textRef} placeholder="Digite seu texto aqui..." rows={5} className='form-control mt-3' value={message} onChange={(e) => setMessage(e.target.value)} />
        
        <PreviewMessage template={message} member={nav.member} />

        {changed && <SavingButton onClick={save} saving={saving} icon='fa-save' className='btn btn-primary mt-3'>Salvar Template</SavingButton>}
        
        <hr />
        <div className='members-nav text-center'>
            {nav.current} / {nav.total}  
            <button className='btn btn-primary ms-2' onClick={() => setNav(nav.prev())} disabled={!nav.hasPrev}><i className='fa fa-arrow-left'></i></button>
            <button className='btn btn-primary ms-2' onClick={() => setNav(nav.next())} disabled={!nav.hasNext}><i className='fa fa-arrow-right'></i></button>
        </div>
    </>
}

export function useTemplatesLoader(templates: ExtendedTemplate[]){
    const {state:{viewCampaign}} = useContext(CampaignContext)
    const setTemplates = useSetTemplates()
    useEffect(()=>{
        if(templates) return;
        (async function (){
            const templates = await loadTemplates(viewCampaign.id);
            setTemplates(templates);
        })();
    }, [viewCampaign.id])
}